import React from 'react'
import { ChallengeService, UserService } from '@duck/api-definitions'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { useTar } from '@/modules'
import { CopyableId } from '@duck/ui'
import { formatChallengeStatus } from '@components/AdminChallenges/adminChallenges.utils'
import { formatWorkTime } from '@/utils/time.utils'

interface ChallengesSectionProps {
  challenges: UserService.AdminUserChallenge[]
}

const STATUS_COLORS: Record<string, string> = {
  finished: 'bg-success/10 text-success',
  ongoing: 'bg-blue-500/10 text-blue-600',
  aborted: 'bg-error/10 text-error',
  submitted: 'bg-warning/10 text-warning',
}

export const ChallengesSection: React.FC<ChallengesSectionProps> = ({ challenges }) => {
  const { t } = useTar()

  const openChallenge = (slug?: string) => {
    if (!slug) return
    window.open(`/admin/challenges/${encodeURIComponent(slug)}`, '_blank', 'noopener,noreferrer')
  }

  if (challenges.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        {t('challenges.noChallenges')}
      </div>
    )
  }

  const grades = challenges.flatMap((c) => (typeof c.feedbackGrade === 'number' ? [c.feedbackGrade] : []))
  const averageGrade = grades.length > 0 ? (grades.reduce((sum, g) => sum + g, 0) / grades.length).toFixed(1) : '-'
  const totalWorkTime = challenges.reduce((sum, c) => sum + (c.workTime ?? 0), 0)
  const metrics = [
    { label: t('challenges.metricsCount'), value: challenges.length },
    { label: t('challenges.metricsAverageGrade'), value: averageGrade },
    { label: t('challenges.metricsTotalWorkTime'), value: formatWorkTime(totalWorkTime, t, { short: true }) },
  ]

  return (
    <div className="space-y-4">
      {challenges.length > 1 && (
        <div className="grid grid-cols-3 gap-4 rounded-lg border border-border bg-muted/5 p-4">
          {metrics.map(({ label, value }) => (
            <div key={label} className="space-y-1 text-center">
              <div className="text-xs font-medium text-muted-foreground">{label}</div>
              <div className="text-2xl font-semibold text-foreground tabular-nums">{value}</div>
            </div>
          ))}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-muted-foreground">
              <th className="text-start py-2 px-3 font-medium">{t('common.id')}</th>
              <th className="text-start py-2 px-3 font-medium">{t('tasks.task')}</th>
              <th className="text-start py-2 px-3 font-medium">{t('common.assignment')}</th>
              <th className="text-start py-2 px-3 font-medium">{t('common.status')}</th>
              <th className="text-start py-2 px-3 font-medium">{t('common.started')}</th>
              <th className="text-start py-2 px-3 font-medium">{t('common.submitted')}</th>
              <th className="text-start py-2 px-3 font-medium">{t('common.grade')}</th>
            </tr>
          </thead>
          <tbody>
            {challenges.map((c) => (
              <tr
                key={c.challengeId}
                onClick={() => openChallenge(c.slug)}
                title={c.slug ? t('challenges.openChallengePage') : undefined}
                className={`border-b border-border/50 hover:bg-muted/5 ${c.slug ? 'cursor-pointer' : ''}`}
              >
                <td className="py-2">
                  <CopyableId value={c.challengeId} />
                </td>
                <td className="py-2 px-3 ps-0">{c.taskName || c.slug}</td>
                <td className="py-2 px-3" title={c.assignmentDeleted ? t('challenges.assignmentDeleted') : undefined}>
                  {c.assignmentId ? (
                    <CopyableId
                      value={c.assignmentId}
                      className={c.assignmentDeleted ? 'text-error' : undefined}
                    />
                  ) : (
                    '-'
                  )}
                </td>
                <td className="py-2 px-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-muted/10 text-muted-foreground'}`}>
                    {t(formatChallengeStatus(c.status as ChallengeService.ChallengeStatusEnum))}
                  </span>
                  {c.deleted && (
                    <span className="ms-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-error/10 text-error">
                      {t('common.deleted')}
                    </span>
                  )}
                </td>
                <td className="py-2 px-3">
                  <RelativeDateTime isoDateString={c.startTime} />
                </td>
                <td className="py-2 px-3">
                  {c.endTime ? <RelativeDateTime isoDateString={c.endTime} /> : '-'}
                </td>
                <td className="py-2 px-3">
                  {c.feedbackGrade !== undefined ? c.feedbackGrade : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
